import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalHeader,
  ModalSubmitButton,
  NewCheckboxField,
  NewRadioField,
  SectionWithBorder,
  SectionWithBorderField,
  showModal,
} from '@edna/components';
import {FieldError} from '@edna/components/FormField';
import {Icon, Stack} from '@edna/components/primitives';
import {ECurrency} from '@edna/utils';
import {ECurrencySymbol} from '@edna/utils/formatMoney';

import {Text} from 'src/components/primitives';
import {useGetTenantDataQuery} from 'src/containers/User';
import {CardIcon, PayKeeperLogoIcon, SBPLogoIcon} from 'src/icons';
import {joiResolver} from 'src/utils/validator';

import {
  useCreatePayKeeperOrderMutation,
  useGetPayKeeperInfoQuery,
  usePayWithPayKeeperMutation,
} from '../api';
import * as S from '../style';

const sendForm = (action: string, values: Record<string, string | number | null | undefined>) => {
  const form = document.createElement('form');

  form.action = action;
  form.method = 'POST';
  form.hidden = true;

  Object.entries(values)
    .filter(([, value]) => !!value)
    .forEach(([key, value]) => {
      const input = document.createElement('input');

      input.type = 'text';
      input.name = key;
      input.value = String(value);
      form.appendChild(input);
    });

  const button = document.createElement('button');

  button.type = 'submit';
  form.appendChild(button);

  document.body.appendChild(form);
  button.click();
};

type TFormData = {
  sum: number;
  isNewCard: boolean;
  isSaveCard?: boolean;
};

const PayKeeperSettingsModal = React.memo<{closeModal: TEmptyFunction}>(({closeModal}) => {
  const {t} = useTranslation();

  const {data: payKeeperInfo} = useGetPayKeeperInfoQuery();
  const [createPayKeeperOrder, {isLoading}] = useCreatePayKeeperOrderMutation();
  const [payWithPayKeeper, {isLoading: isLoadingPayment}] = usePayWithPayKeeperMutation();
  const {data: tenantData} = useGetTenantDataQuery();

  const methods = useForm<TFormData>({
    mode: 'all',
    resolver: joiResolver((joi) =>
      joi.object({
        sum: joi.number().min(1).required(), // min(MIN_PAYKEEPER_AMOUNT) после тестирования
      }),
    ),
    defaultValues: {isNewCard: false},
  });

  const sumError = methods.formState.errors.sum;
  const currency = ECurrencySymbol[tenantData?.defaultCurrency ?? ECurrency.RUB];
  const isNewCard = methods.watch('isNewCard');
  const hasSavedCard = !!payKeeperInfo?.maskedCardNumber;

  const handleSave = React.useCallback(
    async (formValues) => {
      const isNeedSaveCard = formValues.isNewCard && formValues.isSaveCard;

      if (!isNeedSaveCard && hasSavedCard) {
        payWithPayKeeper(formValues.sum);
        closeModal();

        return;
      }

      try {
        const orderInfo = await createPayKeeperOrder({
          sum: formValues.sum,
          createBinding: formValues.isSaveCard,
        }).unwrap();

        sendForm(`${orderInfo.paykeeperServerUrl}/create/`, {
          orderid: orderInfo.orderId,
          clientid: orderInfo.clientId,
          client_email: orderInfo.clientEmail,
          service_name: orderInfo.serviceName,
          sum: formValues.sum,
          msgtype: formValues.isSaveCard ? 'createbinding' : null,
        });

        closeModal();
      } catch (e) {}
    },
    [closeModal, hasSavedCard],
  );

  return (
    <Modal width="530px">
      <FormProvider {...methods}>
        <ModalHeader>{t('BalanceReplenishment:payKeeperSettingsModal.title')}</ModalHeader>
        <S.PayKeeperModalContent>
          <Stack direction="ROW" size="6" justify="center" isFullWidth>
            <PayKeeperLogoIcon />
            <SBPLogoIcon />
          </Stack>
          <SectionWithBorderField
            name="sum"
            label={t('BalanceReplenishment:payKeeperSettingsModal.amount')}
            hint={t('BalanceReplenishment:payKeeperSettingsModal.minAmount', {currency})}
            htmlFor="sum"
          >
            <Stack size="2" direction="ROW" align="center">
              <S.PayKeeperNumberInputField name="sum" showError={false} />
              <Text size="S">{currency}</Text>
            </Stack>
            {!!sumError && (
              <FieldError>{t(sumError?.message ?? '', sumError?.ref?.value)}</FieldError>
            )}
          </SectionWithBorderField>
          {!hasSavedCard && (
            <SectionWithBorder>
              <NewCheckboxField name="isSaveCard">
                {t('BalanceReplenishment:payKeeperSettingsModal.saveCard')}
              </NewCheckboxField>
            </SectionWithBorder>
          )}
          {hasSavedCard && (
            <SectionWithBorder
              label={t('BalanceReplenishment:payKeeperSettingsModal.chooseCard')}
              size="2"
            >
              <NewRadioField name="isNewCard" type="radio" value={false} noMargin>
                {t('BalanceReplenishment:payKeeperSettingsModal.savedCard')}
              </NewRadioField>
              {!isNewCard && (
                <S.NestedBlock>
                  <Text color="grey400" size="XS">
                    <Stack direction="ROW" align="center" size="2">
                      <Icon as={CardIcon} size="16px" />
                      {payKeeperInfo.maskedCardNumber}
                    </Stack>
                  </Text>
                </S.NestedBlock>
              )}
              <NewRadioField name="isNewCard" type="radio" value={true} noMargin>
                {t('BalanceReplenishment:payKeeperSettingsModal.newCard')}
              </NewRadioField>
              {isNewCard && (
                <S.NestedBlock>
                  <NewCheckboxField name="isSaveCard">
                    {t('BalanceReplenishment:payKeeperSettingsModal.replaceCard')}
                  </NewCheckboxField>
                </S.NestedBlock>
              )}
            </SectionWithBorder>
          )}
          <Text size="S" secondary>
            {t('BalanceReplenishment:payKeeperSettingsModal.acceptanceInfo')}
          </Text>
        </S.PayKeeperModalContent>
        <ModalActions>
          <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
          <ModalSubmitButton
            onClick={methods.handleSubmit(handleSave)}
            disabled={isLoading || isLoadingPayment}
          >
            {t('BalanceReplenishment:payKeeperSettingsModal.pay')}
          </ModalSubmitButton>
        </ModalActions>
      </FormProvider>
    </Modal>
  );
});

export const showPayKeeperOnetimePaymentSettingsModal = () =>
  showModal((closeModal) => <PayKeeperSettingsModal closeModal={closeModal} />);
